/* ============================================================
   HQ Custom Product Template - hq-pdp.js
   Presentation-only behaviour: FAQ accordion, paired-products
   carousel + recommendations self-fetch, scroll reveal.
   Zero commerce logic: cart, variants, money and gallery are
   the theme's own machinery.
   ============================================================ */
(function () {
  'use strict';

  /* every hqx section includes this script for portability, so the file can be
     parsed several times per page; run the body only once. */
  if (window.__hqxPdpLoaded) { return; }
  window.__hqxPdpLoaded = true;

  document.documentElement.classList.add('js-reveal');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- FAQ accordion: one open at a time, animated height ---- */
  function initAccordion(accordion) {
    if (!accordion || accordion.dataset.hqInit) return;
    accordion.dataset.hqInit = '1';
    var items = Array.prototype.slice.call(accordion.querySelectorAll('.faq__item'));

    function setHeight(item, open) {
      var answer = item.querySelector('.faq__answer');
      var btn = item.querySelector('.faq__question');
      if (open) {
        item.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        answer.style.height = reduceMotion ? 'auto' : answer.scrollHeight + 'px';
      } else {
        if (!reduceMotion && answer.style.height === 'auto') {
          answer.style.height = answer.scrollHeight + 'px';
          void answer.offsetHeight; /* reflow so the close transition runs */
        }
        item.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        answer.style.height = reduceMotion ? '0' : '0px';
      }
    }

    items.forEach(function (item) {
      var answer = item.querySelector('.faq__answer');
      answer.addEventListener('transitionend', function () {
        if (item.classList.contains('is-open')) { answer.style.height = 'auto'; }
      });
      item.querySelector('.faq__question').addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');
        items.forEach(function (other) {
          if (other !== item && other.classList.contains('is-open')) { setHeight(other, false); }
        });
        setHeight(item, !isOpen);
      });
      if (item.classList.contains('is-open')) { answer.style.height = 'auto'; }
    });
  }

  /* ---- paired carousel: scroll by one card, disable at ends ---- */
  function initCarousel(carousel) {
    if (!carousel || carousel.dataset.hqInit) return;
    carousel.dataset.hqInit = '1';
    var track = carousel.querySelector('[data-hq-carousel-track]');
    var prev = carousel.querySelector('[data-hq-carousel-prev]');
    var next = carousel.querySelector('[data-hq-carousel-next]');
    if (!track || !prev || !next) return;

    function cardStep() {
      var card = track.querySelector('.paired__card');
      if (!card) { return 200; }
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
      return card.getBoundingClientRect().width + gap;
    }

    function updateNav() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= maxScroll - 4;
    }

    prev.addEventListener('click', function () { track.scrollBy({ left: -cardStep(), behavior: 'smooth' }); });
    next.addEventListener('click', function () { track.scrollBy({ left: cardStep(), behavior: 'smooth' }); });
    track.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', updateNav);
    updateNav();
  }

  /* ---- recommendations self-fetch (Dawn pattern) ----
     The paired section renders itself server-side via the Section
     Rendering API when no manual paired_products are set. The
     response is this store's own Liquid for this same section,
     parsed with DOMParser and grafted with replaceChildren. */
  function initRecommendations(host) {
    if (!host || host.dataset.hqInit) return;
    host.dataset.hqInit = '1';
    var url = host.dataset.url;
    if (!url) return;

    function hideSection() {
      var section = host.closest('.hqx-pdp');
      if (section) { section.classList.add('is-hidden'); }
    }

    fetch(url)
      .then(function (response) { return response.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var inner = doc.querySelector('[data-hq-recommendations]');
        if (inner && inner.children.length) {
          host.replaceChildren.apply(host, Array.prototype.slice.call(inner.children));
          initCarousel(host.querySelector('[data-hq-carousel]'));
        } else if (host.dataset.fallbackUrl && !host.dataset.fallbackTried) {
          host.dataset.fallbackTried = '1';
          host.dataset.url = host.dataset.fallbackUrl;
          delete host.dataset.hqInit;
          initRecommendations(host);
        } else {
          hideSection();
        }
      })
      .catch(hideSection);
  }

  /* ---- recently viewed: localStorage history rendered as V2 cards ----
     No Shopify "recently viewed" object exists, so each product page records
     itself (front of a capped list) and this builds cards for the others using
     the same markup as hqx-paired-card. Cards are built with safe DOM methods
     (no innerHTML). Reveals only if there is history. */
  function safeUrl(u) {
    u = String(u == null ? '' : u);
    /* allow site-relative (/path), protocol-relative (//cdn...) and http(s) URLs;
       blocks javascript:, data: and other schemes */
    return (/^\/\//.test(u) || /^\/(?!\/)/.test(u) || /^https?:\/\//.test(u)) ? u : '';
  }

  function buildRecentCard(p) {
    var li = document.createElement('li');
    li.className = 'paired__card';
    var url = safeUrl(p.url);

    var media = document.createElement('a');
    media.href = url;
    media.className = 'media paired__media';
    media.setAttribute('aria-label', p.title || '');
    var imgUrl = safeUrl(p.image);
    if (imgUrl) {
      var img = document.createElement('img');
      img.src = imgUrl;
      img.alt = p.title || '';
      img.loading = 'lazy';
      img.width = 380;
      img.height = 380;
      media.appendChild(img);
    }
    li.appendChild(media);

    var body = document.createElement('div');
    body.className = 'paired__body';
    if (p.vendor) {
      var vendor = document.createElement('p');
      vendor.className = 'paired__vendor';
      vendor.textContent = p.vendor;
      body.appendChild(vendor);
    }
    var titleP = document.createElement('p');
    titleP.className = 'paired__title';
    var titleA = document.createElement('a');
    titleA.href = url;
    titleA.textContent = p.title || '';
    titleP.appendChild(titleA);
    body.appendChild(titleP);

    var priceP = document.createElement('p');
    priceP.className = 'paired__price';
    var price = document.createElement('span');
    price.className = 'hdt-money';
    price.textContent = p.price || '';
    priceP.appendChild(price);
    if (p.onSale && p.compare) {
      var cmpWrap = document.createElement('span');
      cmpWrap.className = 'hdt-compare-at-price';
      var cmp = document.createElement('span');
      cmp.className = 'hdt-money';
      cmp.textContent = p.compare;
      cmpWrap.appendChild(cmp);
      priceP.appendChild(cmpWrap);
    }
    body.appendChild(priceP);
    li.appendChild(body);
    return li;
  }

  function initRecentlyViewed() {
    var KEY = 'hqx-recent';
    var cur = window.hqxRecentPush;
    var list = [];
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { list = []; }
    if (!Array.isArray(list)) { list = []; }

    if (cur && cur.handle) {
      list = list.filter(function (p) { return p && p.handle !== cur.handle; });
      list.unshift(cur);
      list = list.slice(0, 12);
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }

    var wrap = document.querySelector('[data-hq-recently]');
    var track = wrap && wrap.querySelector('[data-hq-recently-track]');
    if (!wrap || !track) { return; }

    var limit = (cur && cur.limit) || 10;
    var others = list.filter(function (p) {
      return p && p.handle && p.url && (!cur || p.handle !== cur.handle);
    }).slice(0, limit);
    if (!others.length) { return; }

    track.textContent = '';
    others.forEach(function (p) { track.appendChild(buildRecentCard(p)); });

    wrap.classList.remove('is-hidden');
    initCarousel(wrap.querySelector('[data-hq-carousel]'));
  }

  /* ---- modal: full-description popup (fixed height, scrolls inside) ---- */
  function initModals() {
    document.querySelectorAll('[data-hq-modal-open]').forEach(function (trigger) {
      if (trigger.dataset.hqInit) { return; }
      trigger.dataset.hqInit = '1';
      var modal = document.getElementById(trigger.getAttribute('data-hq-modal-open'));
      if (!modal) { return; }
      var lastFocus = null;
      function open() {
        lastFocus = document.activeElement;
        modal.hidden = false;
        document.documentElement.classList.add('hq-modal-open');
        var closeBtn = modal.querySelector('.hq-modal__close');
        if (closeBtn) { closeBtn.focus(); }
      }
      function close() {
        modal.hidden = true;
        document.documentElement.classList.remove('hq-modal-open');
        if (lastFocus && lastFocus.focus) { lastFocus.focus(); }
      }
      trigger.addEventListener('click', open);
      modal.querySelectorAll('[data-hq-modal-close]').forEach(function (btn) {
        btn.addEventListener('click', close);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) { close(); }
      });
    });
  }

  /* ---- scroll reveal: subtle fade-up, fail-safe ---- */
  function initReveal() {
    var targets = document.querySelectorAll('.hq-reveal');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion) {
      targets.forEach(function (t) { t.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    targets.forEach(function (t) { io.observe(t); });
    /* fail-safe: never leave content hidden if IO misbehaves */
    setTimeout(function () {
      targets.forEach(function (t) { t.classList.add('is-visible'); });
    }, 2500);
  }

  function init() {
    document.querySelectorAll('[data-hq-accordion]').forEach(initAccordion);
    initRecentlyViewed();
    initModals();
    document.querySelectorAll('[data-hq-carousel]').forEach(initCarousel);
    document.querySelectorAll('[data-hq-recommendations-host]').forEach(initRecommendations);
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* re-init inside the theme editor when a section is re-rendered */
  document.addEventListener('shopify:section:load', init);
})();
